import {
  CompetencePassport,
  LearningOutcome,
  Level,
  Skill,
} from "@/server/backend-api/generated";
import { Section, SectionContainer } from "@/components/Sections/Sections";
import { Assert } from "@/utils/assert";
import { getLevelString } from "@/server/backend-api/enumToText";
import { IconIconInfo } from "@/components/Icons";
import { LearningOutcomeModal } from "@/components/Modal/LearningOutcomeModal";
import {
  CompetenceBaseTreeEx,
  CompetenceBaseWithChildrenEx,
} from "@/server/backend-api";
import { useState } from "react";
import {
  CompetenceRelationshipMap,
  getTopicsWithAllSubtopics,
} from "@/utils/competenceTree";
import { capitalize } from "@/utils/text";
import { mcn } from "@/utils";
import { MotivationScanAnswers } from "@/utils/motivationScan";
import Button from "../Button";
import config from "../../config";
import { useCompetencePassportStats } from "@/hooks/useCompetencePassportStats";
import {
  CompetenceGraph,
  CompetenceGraphProps,
} from "@/components/CompetenceGraph/CompetenceGraph";
import IconVector from "@/components/Icons/IconVector";
import { CompetenceGraphModal } from "@/components/CompetenceGraph/CompetenceGraphModal";
import { CompetenceGraphLegend } from "@/components/CompetenceGraph/CompetenceGraphLegend";
import Link from "next/link";
import { addTestId, TestIds } from "@/components/testIds";

export function SkillTab(props: { skill: Skill }) {
  const { skill } = props;
  const url = skill.url;
  return (
    <Link
      href={url ?? ""}
      rel={"noopener noreferrer"}
      target={"_blank"}
      prefetch={false}
      className={mcn(
        "w-fit px-[8px] py-[6px] bg-green-100/30 border border-green-100 rounded-3 text-style-caption",
        "hover:bg-[transparent] hover:cursor-pointer"
      )}
      {...addTestId(TestIds.SkillTab)}
    >
      {capitalize(skill.name ?? "")}
    </Link>
  );
}

export function LevelTab(props: { level: Level }) {
  const { level } = props;
  return (
    <div className="px-[6px] py-[4px] bg-grey-120 rounded-3 text-style-caption">
      {getLevelString(level)}
    </div>
  );
}

interface TabProps {
  tabs: {
    name: string;
  }[];
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  className?: string;
  tabClassName?: string;
}

export function Tabs(props: TabProps) {
  const { tabs, selectedIndex, onSelectedIndexChange } = props;
  return (
    <div className={mcn("flex flex-shrink", props.className)}>
      {tabs.map((tab, index) => {
        const isSelected = index === selectedIndex;
        return (
          <button
            key={index}
            className={mcn(
              "h-[40px] py-xs px-4 border border-white focus-visible:default-focus",
              index === 0 && "rounded-l-full",
              index === tabs.length - 1 && "rounded-r-full",
              isSelected && "bg-white/20",
              !isSelected && "bg-grey-40/10 hover:bg-white/20",
              props.tabClassName
            )}
            onClick={() => onSelectedIndexChange(index)}
          >
            <span
              className={mcn(
                isSelected && "text-style-subtitle2",
                !isSelected && "text-style-body2"
              )}
            >
              {tab.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function LearningOutcomeSections(props: {
  learningOutcomes: LearningOutcome[];
  tree: CompetenceBaseTreeEx;
  answers: MotivationScanAnswers | null;
  competencePassport: CompetencePassport | null;
  outcomesSectionId?: string;
  potentialProgressSectionId?: string;
  isSignedIn: boolean;
}) {
  const { learningOutcomes, tree, answers, competencePassport, isSignedIn } =
    props;
  const hasDoneMotivationScan = answers !== null;
  const relationshipMap = new CompetenceRelationshipMap(tree);
  const allTopics = getTopicsWithAllSubtopics(tree);
  const [modal, setModal] = useState<{
    open: boolean;
    outcome: LearningOutcome;
  }>({
    open: false,
    outcome: {
      id: "temp",
      name: null,
      description: null,
      competenceAndLevels: [],
    },
  });
  const passportStats = useCompetencePassportStats(
    tree,
    learningOutcomes,
    competencePassport,
    answers
  );

  /**
   * Maps the learning outcomes to the parent competences, without duplictes
   * @param outcomes
   * @param allTopics All topics.
   */
  function mapOutcomeToTopics(
    outcomes: LearningOutcome[],
    allTopics: CompetenceBaseWithChildrenEx[]
  ): CompetenceBaseWithChildrenEx[] {
    const topics: CompetenceBaseWithChildrenEx[] = [];
    for (const outcome of outcomes) {
      const competenceAndLevels = outcome.competenceAndLevels ?? [];
      for (const competenceAndLevel of competenceAndLevels) {
        Assert.notNull(competenceAndLevel.competenceId);
        const topicId = relationshipMap.getTopicId(
          competenceAndLevel.competenceId
        );
        Assert.notNull(topicId);
        const topic = allTopics.find((c) => c.id === topicId);
        Assert.notNull(topic);
        topics.push(topic);
      }
    }
    const withoutDuplicates = topics.filter(
      (c, index) => topics.findIndex((c2) => c2.id === c.id) === index
    );
    return withoutDuplicates;
  }

  const topicsInUseByOutcomes = mapOutcomeToTopics(learningOutcomes, allTopics);
  // Do not display anything if there are no learning outcomes.
  if (learningOutcomes.length === 0) {
    return <></>;
  }

  return (
    <>
      <Section
        id={props.outcomesSectionId}
        fillScreenHeight={false}
        color="blue"
      >
        <LearningOutcomeModal
          open={modal.open}
          onClose={() =>
            setModal((prev) => {
              return {
                ...prev,
                open: false,
              };
            })
          }
          outcome={
            modal.outcome ?? {
              id: "temp",
              name: null,
              description: null,
              competenceAndLevels: [],
            }
          }
          tree={tree}
        />
        <SectionContainer defaultPadding={true}>
          <div className="flex flex-col items-center">
            <h2 className="text-style-headline2">Learning outcomes</h2>
            <div className="w-full grid grid-cols-1 tablet:grid-cols-2 mt-2xl gap-x-2xl gap-y-3xl">
              {learningOutcomes.map((outcome, outcomeIndex) => {
                const uniqueSkillsPerOutcome: Skill[] = [];
                const competenceAndLevels = outcome.competenceAndLevels ?? [];
                for (const competenceAndLevel of competenceAndLevels) {
                  const skills = competenceAndLevel.skills ?? [];
                  for (const skill of skills) {
                    if (
                      !uniqueSkillsPerOutcome.some((s) => s.id === skill.id)
                    ) {
                      uniqueSkillsPerOutcome.push(skill);
                    }
                  }
                }

                return (
                  <div key={outcomeIndex} className="col-span-1">
                    <h3 className="text-style-subtitle2">{outcome.name}</h3>
                    <p className={"mt-xs text-style-body2"}>
                      {outcome.description}
                    </p>
                    <h4 className="mt-lg text-style-overline">ESCO SKILLS</h4>
                    <div className="flex flex-col mt-3xs gap-y-sm text-style-caption">
                      {uniqueSkillsPerOutcome.map((skill, index) => (
                        <SkillTab key={index} skill={skill} />
                      ))}
                    </div>
                    <button
                      onClick={() =>
                        setModal({
                          open: true,
                          outcome: outcome,
                        })
                      }
                      className="py-[4px] px-[8px] mt-lg bg-white/50 hover:bg-white/30 text-black text-style-caption rounded-l-full rounded-r-full flex items-center align-middle"
                    >
                      <IconIconInfo className={"w-5 h-5"} />
                      <span className={"ml-2xs"}>Read more</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionContainer>
      </Section>
      <Section id={props.potentialProgressSectionId} color={"light-blue"}>
        <SectionContainer grid gridNoYGap className="gap-y-lg">
          <div className={"col-span-full tablet:col-span-8 flex flex-col"}>
            <h2 className={"text-style-headline2 text-light-yellow"}>
              Potential progress
            </h2>
            <p className={"text-style-body1"}>
              This graph shows the potential you could reach if you take this
              learning opportunity and how it fits your goal. You can also make
              a comparison to your current and potential competencies by
              pressing the buttons.
            </p>
          </div>
          <div className="col-span-full tablet:col-span-4 pt-lg">
            <CompetenceGraphLegend />
          </div>
          <div className={"col-span-full pt-lg tablet:pt-2xl"}></div>
          {topicsInUseByOutcomes.map((topic, index) => {
            const isFirst = index === 0;
            const potentialGraphSubtopics =
              passportStats.mapTopicToPotentialCompletedCompetenceGraph(
                topic.id
              );
            const completedGraphSubtopics = isSignedIn
              ? passportStats.mapTopicToCompletedCompetenceGraph(topic.id)
              : [];

            return (
              <div
                key={index}
                className={mcn(
                  "col-span-full",
                  topic.children.length >= 7
                    ? "desktop:col-span-6"
                    : "tablet-lg:col-span-6"
                )}
              >
                <CompetenceGraphContainer
                  topic={topic}
                  tabs={[
                    CompetenceGraphTab.PotentialCompetences,
                    CompetenceGraphTab.CurrentCompetences,
                  ]}
                  potentialGraphProps={{
                    subtopics: potentialGraphSubtopics,
                    showMobileFlipScreenTip: isFirst,
                  }}
                  completedGraphProps={{
                    subtopics: completedGraphSubtopics,
                    showMobileFlipScreenTip: isFirst,
                  }}
                  isSignedIn={isSignedIn}
                  showYourInspirationInModal={false}
                />
              </div>
            );
          })}
          {!hasDoneMotivationScan && (
            <div className={"col-span-full mt-2xl laptop:mt-3xl"}>
              <MotivationScanCta />
            </div>
          )}
        </SectionContainer>
      </Section>
    </>
  );
}

function MotivationScanCta() {
  return (
    <div className="flex flex-col gap-y-sm items-center">
      <h3 className="text-style-headline3">
        Not sure which competencies suit you?
      </h3>
      <p className="text-style-body1">
        Take our{" "}
        <Link className={"link"} href={"/motivation-scan/onboarding"}>
          motivation scan
        </Link>{" "}
        to find learning opportunities that will help you reach your potential
        goal and growth.
      </p>
    </div>
  );
}

export enum CompetenceGraphTab {
  PotentialCompetences = 0,
  CurrentCompetences = 1,
}

interface CompetenceGraphContainerProps {
  topic: CompetenceBaseWithChildrenEx;
  potentialGraphProps: CompetenceGraphProps;
  completedGraphProps: CompetenceGraphProps;
  tabs: CompetenceGraphTab[];
  isSignedIn: boolean;
  showYourInspirationInModal: boolean;
}

export function CompetenceGraphContainer(props: CompetenceGraphContainerProps) {
  const {
    topic,
    potentialGraphProps,
    completedGraphProps,
    isSignedIn,
    showYourInspirationInModal,
    tabs,
  } = props;

  const [selectedTab, setSelectedTab] = useState<CompetenceGraphTab>(tabs[0]);
  const [showModal, setShowModal] = useState(false);

  const isTabBarVisible = tabs.length > 1;
  const selectedGraphProps =
    selectedTab === CompetenceGraphTab.PotentialCompetences
      ? potentialGraphProps
      : completedGraphProps;
  const isNotSignedInAndCurrentTabSelected =
    !isSignedIn && selectedTab === CompetenceGraphTab.CurrentCompetences;

  // We want the legends of the graph to overflow into the padding of the parent container.
  const GRAPH_PADDING_NEGATION = "-ml-6 -mb-6";
  return (
    <>
      <CompetenceGraphModal
        open={showModal}
        onClose={() => setShowModal(false)}
        graphSubtopics={selectedGraphProps.subtopics}
        topicName={topic.name ?? ""}
        showYourInspiration={showYourInspirationInModal}
      />

      <div>
        <div className={"rounded-2 bg-blue flex flex-col"}>
          <div
            className={
              "relative bg-green-100 rounded-t-2 h-2xl flex items-center justify-center"
            }
          >
            <div
              className={
                "absolute hidden tablet:flex right-0 h-full items-center"
              }
            >
              {!isNotSignedInAndCurrentTabSelected && (
                <button
                  className={"p-1 mr-4 hover:opacity-50"}
                  onClick={() => setShowModal(true)}
                >
                  <IconVector className={"w-5 h-5 text-black"} />
                </button>
              )}
            </div>

            <h3 className={"text-style-subtitle1 text-blue p-md"}>
              <Link className="underline hover:no-underline" href={topic.url}>{topic.name}</Link>
            </h3>
          </div>
          <div
            className={
              "flex flex-col p-8 justify-center min-h-[370px] relative"
            }
          >
            <div className="flex justify-center">
              {isTabBarVisible && (
                <div className="my-md px-md flex justify-center absolute top-0">
                  {/*Lock individual tab sizes to prevent "jumping" when text style changes.*/}
                  <Tabs
                    tabs={[
                      {
                        name: "Potential",
                      },
                      {
                        name: "Current",
                      },
                    ]}
                    selectedIndex={selectedTab}
                    onSelectedIndexChange={(newIndex) =>
                      setSelectedTab(newIndex)
                    }
                    className={"desktop:hidden"}
                    tabClassName={"w-[100px]"}
                  />
                  <Tabs
                    tabs={[
                      {
                        name: "Potential",
                      },
                      {
                        name: "Current",
                      },
                    ]}
                    selectedIndex={selectedTab}
                    onSelectedIndexChange={(newIndex) =>
                      setSelectedTab(newIndex)
                    }
                    className={"hidden desktop:block"}
                    tabClassName={"desktop:w-[216px]"}
                  />
                </div>
              )}
            </div>
            <div
              className={mcn(
                "overflow-x-auto",
                !isNotSignedInAndCurrentTabSelected && GRAPH_PADDING_NEGATION
              )}
            >
              {selectedTab === CompetenceGraphTab.PotentialCompetences && (
                <div className={mcn(isTabBarVisible && "mt-2xl")}>
                  <CompetenceGraph {...potentialGraphProps} />
                </div>
              )}
              {selectedTab === CompetenceGraphTab.CurrentCompetences &&
                (isSignedIn ? (
                  <div className={mcn(isTabBarVisible && "mt-2xl")}>
                    <CompetenceGraph {...completedGraphProps} />
                  </div>
                ) : (
                  <SignInToViewCurrentCta />
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function SignInToViewCurrentCta() {
  return (
    <div className={"flex flex-col items-center h-full gap-y-md"}>
      <p className="text-style-body1 tablet:max-w-[348px] flex-grow-0 flex-shrink-0">
        Sign in to save your results and view your competencies.
      </p>
      <div className="flex flex-col tablet:flex-row items-center gap-xs">
        <Button label="Sign in" href="/signin" />
        <span className="text-style-subtitle2">OR</span>
        <Button
          kind={"secondary"}
          label="Create account"
          href={config.auth.signUpUri}
        />
      </div>
    </div>
  );
}
